import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, FormControl, FormLabel, Input, Select, Button, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Slider, SliderTrack, SliderFilledTrack, SliderThumb } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinanceToolsBorrowingPower: React.FC = () => {
  const [income, setIncome] = useState(80000);
  const [expenses, setExpenses] = useState(2000);
  const [debts, setDebts] = useState(500);
  const [dependants, setDependants] = useState(0);
  const [loanTerm, setLoanTerm] = useState(30);
  const [interestRate, setInterestRate] = useState(6.5);

  const calculateBorrowingPower = () => {
    const monthlyIncome = income / 12;
    const monthlyExpenses = expenses + debts;
    const availableIncome = monthlyIncome - monthlyExpenses - (dependants * 300);
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;

    if (availableIncome <= 0 || monthlyRate <= 0) return 0;

    const borrowingPower = availableIncome * ((1 - Math.pow(1 + monthlyRate, -numPayments)) / monthlyRate);
    return Math.max(0, Math.round(borrowingPower));
  };

  const borrowingPower = calculateBorrowingPower();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/tools/borrowing-power"
        fallbackTitle="Borrowing Power Calculator"
        fallbackDescription="Calculate how much you can borrow"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Finance Section
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Borrowing Power Calculator
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Calculate how much you can borrow</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
          {/* Calculator Form */}
          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="lg">
            <Heading size="md" mb={6}>Enter Your Details</Heading>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Annual Income Before Tax</FormLabel>
                <Input
                  type="number"
                  value={income}
                  onChange={(e) => setIncome(Number(e.target.value))}
                  size="lg"
                />
                <Text fontSize="sm" color="gray.600" mt={1}>${income.toLocaleString()} per year</Text>
              </FormControl>

              <FormControl>
                <FormLabel>Monthly Living Expenses</FormLabel>
                <Input
                  type="number"
                  value={expenses}
                  onChange={(e) => setExpenses(Number(e.target.value))}
                  size="lg"
                />
                <Text fontSize="sm" color="gray.600" mt={1}>${expenses.toLocaleString()} per month</Text>
              </FormControl>

              <FormControl>
                <FormLabel>Monthly Debt Repayments</FormLabel>
                <Input
                  type="number"
                  value={debts}
                  onChange={(e) => setDebts(Number(e.target.value))}
                  size="lg"
                />
                <Text fontSize="sm" color="gray.600" mt={1}>Credit cards, personal loans, car loans</Text>
              </FormControl>

              <FormControl>
                <FormLabel>Number of Dependants</FormLabel>
                <Select
                  value={dependants}
                  onChange={(e) => setDependants(Number(e.target.value))}
                  size="lg"
                >
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4+</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Loan Term: {loanTerm} years</FormLabel>
                <Slider value={loanTerm} onChange={setLoanTerm} min={5} max={30} step={5}>
                  <SliderTrack>
                    <SliderFilledTrack bg="blue.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
              </FormControl>

              <FormControl>
                <FormLabel>Interest Rate: {interestRate}%</FormLabel>
                <Slider value={interestRate} onChange={setInterestRate} min={3} max={10} step={0.1}>
                  <SliderTrack>
                    <SliderFilledTrack bg="green.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
              </FormControl>

              <Button colorScheme="blue" size="lg" onClick={() => calculateBorrowingPower()}>
                Recalculate
              </Button>
            </VStack>
          </Box>

          {/* Results */}
          <Box>
            <Box bg="gradient-to-br" bgGradient="linear(to-br, blue.500, purple.600)" p={8} borderRadius="xl" color="white" textAlign="center" mb={6}>
              <Text fontSize="sm" textTransform="uppercase" mb={2}>Your Estimated Borrowing Power</Text>
              <Heading size="3xl" mb={2}>${borrowingPower.toLocaleString()}</Heading>
              <Text fontSize="sm">Based on the information provided</Text>
            </Box>

            <SimpleGrid columns={2} spacing={4} mb={6}>
              <Stat bg="blue.50" p={4} borderRadius="lg">
                <StatLabel>Monthly Repayment</StatLabel>
                <StatNumber fontSize="xl">${Math.round((borrowingPower * (interestRate / 100 / 12)) / (1 - Math.pow(1 + (interestRate / 100 / 12), -(loanTerm * 12)))).toLocaleString()}</StatNumber>
                <StatHelpText>Approx. at {interestRate}%</StatHelpText>
              </Stat>
              <Stat bg="green.50" p={4} borderRadius="lg">
                <StatLabel>Total Interest</StatLabel>
                <StatNumber fontSize="xl">${Math.round(((borrowingPower * (interestRate / 100 / 12)) / (1 - Math.pow(1 + (interestRate / 100 / 12), -(loanTerm * 12)))) * loanTerm * 12 - borrowingPower).toLocaleString()}</StatNumber>
                <StatHelpText>Over {loanTerm} years</StatHelpText>
              </Stat>
            </SimpleGrid>

            <Box bg="orange.50" p={6} borderRadius="lg" border="1px solid" borderColor="orange.200">
              <Heading size="sm" mb={3}>Important Notes</Heading>
              <VStack align="start" spacing={2} fontSize="sm">
                <Text>• This is an estimate only. Actual borrowing capacity may vary.</Text>
                <Text>• Lenders assess your capacity based on their own criteria.</Text>
                <Text>• Interest rates are subject to change.</Text>
                <Text>• Additional costs (fees, insurance) are not included.</Text>
                <Text>• Speak with a finance broker for an accurate assessment.</Text>
              </VStack>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceToolsBorrowingPower;
